import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Inbox as InboxIcon,
  RefreshCcw,
  Search,
  X,
} from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { api, adminHeaders, clearAdminToken, getAdminToken, API_BASE } from "@/lib/api";

const INQUIRY_STATUSES = [
  "New",
  "Reviewed",
  "Follow Up Sent",
  "Consultation Offered",
  "Consultation Scheduled",
  "Proposal Sent",
  "Closed Won",
  "Closed Lost",
  "Not Aligned",
];

const SPEAKING_STATUSES = [
  "New",
  "Reviewed",
  "Follow Up Sent",
  "Offer Sent",
  "Confirmed",
  "Declined",
  "Closed",
];

const TABS = [
  { key: "districts", label: "Districts", clientType: "District", type: "inquiry" },
  { key: "schools", label: "Schools", clientType: "School", type: "inquiry" },
  { key: "organizations", label: "Organizations", clientType: "Organization", type: "inquiry" },
  { key: "community", label: "Community", clientType: "Community", type: "inquiry" },
  { key: "company", label: "Company", clientType: "Company", type: "inquiry" },
  { key: "speaking", label: "Speaking", type: "speaking" },
  { key: "newsletter", label: "Newsletter", type: "newsletter" },
];

function StatusPill({ value }) {
  return (
    <span
      data-testid={`status-pill-${value.toLowerCase().replace(/\s+/g, "-")}`}
      className="wt-status-pill"
    >
      {value}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminInboxPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [activeKey, setActiveKey] = useState("districts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const activeTab = TABS.find((t) => t.key === activeKey);

  const guard = useCallback(() => {
    if (!getAdminToken()) {
      navigate("/admin", { replace: true });
      return false;
    }
    return true;
  }, [navigate]);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/summary", { headers: adminHeaders() });
      setSummary(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
      }
    }
  }, [navigate]);

  const loadItems = useCallback(
    async (tab) => {
      if (!guard()) return;
      setLoading(true);
      try {
        let url = "";
        if (tab.type === "speaking") url = "/admin/speaking-inquiries";
        else if (tab.type === "newsletter") url = "/admin/newsletter";
        else url = `/admin/inquiries?client_type=${encodeURIComponent(tab.clientType)}`;
        const { data } = await api.get(url, { headers: adminHeaders() });
        setItems(data);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearAdminToken();
          toast.error("Session expired. Please sign in again.");
          navigate("/admin", { replace: true });
        } else {
          toast.error("Could not load this tab.");
        }
      } finally {
        setLoading(false);
      }
    },
    [guard, navigate],
  );

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    setSelected(null);
    setStatusFilter("All");
    setSearch("");
    loadItems(activeTab);
  }, [activeKey, activeTab, loadItems]);

  const refreshAll = async () => {
    await loadSummary();
    await loadItems(activeTab);
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((it) => {
      if (activeTab.type !== "newsletter" && statusFilter !== "All" && it.status_value !== statusFilter) return false;
      if (!term) return true;
      return [
        it.full_name,
        it.email,
        it.organization_name,
        it.service_area,
        it.event_name,
        it.first_name,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(term));
    });
  }, [items, search, statusFilter, activeTab]);

  const updateInquiryStatus = async (id, status_value) => {
    setSaving(true);
    try {
      const isSpeaking = activeTab.type === "speaking";
      const endpoint = isSpeaking
        ? `/admin/speaking-inquiries/${id}`
        : `/admin/inquiries/${id}`;
      const { data } = await api.patch(endpoint, { status_value }, { headers: adminHeaders() });
      setItems((prev) => prev.map((p) => (p.id === id ? data : p)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Status updated to “${status_value}”`);
    } catch {
      toast.error("Could not update status.");
    } finally {
      setSaving(false);
    }
  };

  const downloadCsv = async () => {
    const token = getAdminToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/admin/export/${activeTab.key}.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `wanakee-trask-${activeTab.key}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Could not download CSV.");
    }
  };

  const tabCounts = (key) => {
    if (!summary) return null;
    const map = {
      districts: summary.district,
      schools: summary.school,
      organizations: summary.organization,
      community: summary.community,
      company: summary.company,
      speaking: summary.speaking,
      newsletter: summary.newsletter,
    };
    return map[key] ?? 0;
  };

  return (
    <AdminShell
      title="Lead inbox"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshAll}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-1.5 text-sm px-3 h-9 rounded-md"
            style={{ border: "1px solid var(--wt-border)", backgroundColor: "#fff" }}
          >
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            data-testid="admin-download-csv"
            className="wt-btn-primary h-9 px-3 text-sm"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      }
    >
      <div
        data-testid="admin-tabs"
        role="tablist"
        aria-label="Inbox tabs"
        className="flex flex-wrap gap-2 mb-5"
      >
        {TABS.map((t) => {
          const active = t.key === activeKey;
          const count = tabCounts(t.key);
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveKey(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className="px-3 h-9 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors"
              style={{
                backgroundColor: active ? "var(--wt-primary)" : "#fff",
                color: active ? "var(--wt-bg)" : "var(--wt-text)",
                border: `1px solid ${active ? "var(--wt-primary)" : "var(--wt-border)"}`,
              }}
            >
              {t.label}
              {count !== null && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-5 h-5 rounded-full text-[11px] font-bold px-1.5"
                  style={{
                    backgroundColor: active ? "rgba(250,247,240,0.18)" : "rgba(6,78,59,0.08)",
                    color: active ? "var(--wt-bg)" : "var(--wt-primary)",
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab.type !== "newsletter" && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
          <div className="relative flex-1 max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 wt-text-muted"
            />
            <input
              type="text"
              placeholder="Search name, email, organization…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="wt-input pl-8"
              data-testid="admin-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="wt-input sm:max-w-xs"
            data-testid="admin-status-filter"
          >
            <option value="All">All statuses</option>
            {(activeTab.type === "speaking" ? SPEAKING_STATUSES : INQUIRY_STATUSES).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="wt-card overflow-hidden">
        {loading ? (
          <div className="p-10 text-center wt-text-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <InboxIcon size={28} className="mx-auto wt-text-muted" />
            <p className="mt-3 font-serif text-lg wt-primary">Nothing here yet.</p>
            <p className="mt-1 text-sm wt-text-muted">
              New submissions for this tab will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-table">
              {activeTab.type === "newsletter" ? (
                <NewsletterTable rows={filtered} />
              ) : activeTab.type === "speaking" ? (
                <SpeakingTable rows={filtered} onOpen={setSelected} />
              ) : (
                <InquiryTable rows={filtered} onOpen={setSelected} />
              )}
            </table>
          </div>
        )}
      </div>

      {selected && activeTab.type !== "newsletter" && (
        <Drawer
          item={selected}
          tabType={activeTab.type}
          onClose={() => setSelected(null)}
          onUpdate={updateInquiryStatus}
          saving={saving}
          statuses={activeTab.type === "speaking" ? SPEAKING_STATUSES : INQUIRY_STATUSES}
        />
      )}
    </AdminShell>
  );
}

function NewsletterTable({ rows }) {
  return (
    <>
      <thead className="text-[11px] uppercase tracking-[0.12em] wt-text-muted" style={{ backgroundColor: "rgba(232,226,216,0.35)" }}>
        <tr>
          <th className="text-left font-semibold px-4 py-3">Subscribed</th>
          <th className="text-left font-semibold px-4 py-3">First Name</th>
          <th className="text-left font-semibold px-4 py-3">Email</th>
          <th className="text-left font-semibold px-4 py-3">Source</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} data-testid="newsletter-row" className="border-t" style={{ borderColor: "var(--wt-border)" }}>
            <td className="px-4 py-3 wt-text-muted whitespace-nowrap">{formatDate(r.created_at)}</td>
            <td className="px-4 py-3">{r.first_name || ""}</td>
            <td className="px-4 py-3"><a className="wt-link" href={`mailto:${r.email}`}>{r.email}</a></td>
            <td className="px-4 py-3 wt-text-muted">{r.source || ""}</td>
          </tr>
        ))}
      </tbody>
    </>
  );
}

function InquiryTable({ rows, onOpen }) {
  return (
    <>
      <thead className="text-[11px] uppercase tracking-[0.12em] wt-text-muted" style={{ backgroundColor: "rgba(232,226,216,0.35)" }}>
        <tr>
          <th className="text-left font-semibold px-4 py-3">Submitted</th>
          <th className="text-left font-semibold px-4 py-3">Name</th>
          <th className="text-left font-semibold px-4 py-3">Organization</th>
          <th className="text-left font-semibold px-4 py-3">Service area</th>
          <th className="text-left font-semibold px-4 py-3">Budget</th>
          <th className="text-left font-semibold px-4 py-3">Status</th>
          <th className="text-right font-semibold px-4 py-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((it) => (
          <tr
            key={it.id}
            data-testid="admin-inquiry-row"
            className="border-t cursor-pointer hover:bg-[rgba(232,226,216,0.35)] transition-colors"
            style={{ borderColor: "var(--wt-border)" }}
            onClick={() => onOpen(it)}
          >
            <td className="px-4 py-3 wt-text-muted whitespace-nowrap">{formatDate(it.created_at)}</td>
            <td className="px-4 py-3">
              <div className="font-medium">{it.full_name}</div>
              <div className="text-xs wt-text-muted">{it.email}</div>
            </td>
            <td className="px-4 py-3">{it.organization_name}</td>
            <td className="px-4 py-3">{it.service_area}</td>
            <td className="px-4 py-3 wt-text-muted">{it.budget_range || "—"}</td>
            <td className="px-4 py-3"><StatusPill value={it.status_value} /></td>
            <td className="px-4 py-3 text-right">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(it);
                }}
                className="inline-flex items-center gap-1 text-xs wt-link font-semibold"
              >
                Review <ChevronRight size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );
}

function SpeakingTable({ rows, onOpen }) {
  return (
    <>
      <thead className="text-[11px] uppercase tracking-[0.12em] wt-text-muted" style={{ backgroundColor: "rgba(232,226,216,0.35)" }}>
        <tr>
          <th className="text-left font-semibold px-4 py-3">Submitted</th>
          <th className="text-left font-semibold px-4 py-3">Name</th>
          <th className="text-left font-semibold px-4 py-3">Organization</th>
          <th className="text-left font-semibold px-4 py-3">Event</th>
          <th className="text-left font-semibold px-4 py-3">Date</th>
          <th className="text-left font-semibold px-4 py-3">Status</th>
          <th className="text-right font-semibold px-4 py-3">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((it) => (
          <tr
            key={it.id}
            data-testid="admin-speaking-row"
            className="border-t cursor-pointer hover:bg-[rgba(232,226,216,0.35)] transition-colors"
            style={{ borderColor: "var(--wt-border)" }}
            onClick={() => onOpen(it)}
          >
            <td className="px-4 py-3 wt-text-muted whitespace-nowrap">{formatDate(it.created_at)}</td>
            <td className="px-4 py-3">
              <div className="font-medium">{it.full_name}</div>
              <div className="text-xs wt-text-muted">{it.email}</div>
            </td>
            <td className="px-4 py-3">{it.organization_name}</td>
            <td className="px-4 py-3">{it.event_name}</td>
            <td className="px-4 py-3 wt-text-muted">{it.event_date || "—"}</td>
            <td className="px-4 py-3"><StatusPill value={it.status_value} /></td>
            <td className="px-4 py-3 text-right">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(it);
                }}
                className="inline-flex items-center gap-1 text-xs wt-link font-semibold"
              >
                Review <ChevronRight size={14} />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </>
  );
}

function Drawer({ item, tabType, onClose, onUpdate, saving, statuses }) {
  const [status, setStatus] = useState(item.status_value);

  useEffect(() => {
    setStatus(item.status_value);
  }, [item]);

  const isSpeaking = tabType === "speaking";

  return (
    <div
      data-testid="admin-drawer"
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="flex-1 bg-black/40"
        onClick={onClose}
        aria-label="Close detail"
      />
      <aside
        className="w-full max-w-xl h-full overflow-y-auto"
        style={{ backgroundColor: "var(--wt-bg)", boxShadow: "-12px 0 32px rgba(2,44,34,0.18)" }}
      >
        <div
          className="sticky top-0 z-10 border-b"
          style={{
            borderColor: "var(--wt-border)",
            backgroundColor: "rgba(250,247,240,0.96)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div className="px-6 py-4 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1 text-sm wt-primary"
            >
              <ChevronLeft size={16} /> Back to inbox
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md"
              style={{ border: "1px solid var(--wt-border)" }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <p className="wt-eyebrow">{isSpeaking ? "Speaking inquiry" : "Service inquiry"}</p>
            <h2 className="wt-h2 mt-2">{item.full_name}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm wt-text-muted">
              <span>{item.organization_name}</span>
              <span aria-hidden="true">·</span>
              <a href={`mailto:${item.email}`} className="wt-link">{item.email}</a>
              {item.phone_number && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>{item.phone_number}</span>
                </>
              )}
            </div>
            <p className="mt-2 text-xs wt-text-muted">
              Received {formatDate(item.created_at)}
            </p>
          </div>

          <div className="wt-card p-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-1">
              <label htmlFor="status-select" className="wt-label">
                <span>Status</span>
              </label>
              <select
                id="status-select"
                data-testid="admin-status-select"
                className="wt-input mt-2"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={saving || status === item.status_value}
              onClick={() => onUpdate(item.id, status)}
              data-testid="admin-status-save"
              className="wt-btn-primary h-11 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving…" : "Save status"}
            </button>
          </div>

          {isSpeaking ? <SpeakingDetails item={item} /> : <InquiryDetails item={item} />}
        </div>
      </aside>
    </div>
  );
}

function Detail({ label, value, multiline, link }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <p className="wt-eyebrow">{label}</p>
      <div className={`mt-2 wt-body ${multiline ? "whitespace-pre-wrap" : ""}`} style={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
        {link ? (
          <a href={value} target="_blank" rel="noopener noreferrer" className="wt-link inline-flex items-center gap-1">
            {value} <ExternalLink size={12} />
          </a>
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function InquiryDetails({ item }) {
  return (
    <>
      <Detail label="Role or title" value={item.role_or_title} />
      <Detail label="Client type" value={item.client_type} />
      <Detail label="Service area" value={item.service_area} />
      <Detail label="Organization link" value={item.organization_link} link />
      <Detail label="Needs and pain points" value={item.needs_and_pain_points} multiline />
      <Detail label="Scale or audience size" value={item.scale_or_audience_size} multiline />
      <Detail label="Timeline" value={item.timeline} />
      <Detail label="Budget range" value={item.budget_range} />
      <Detail label="Holds budget" value={item.holds_budget} />
      <Detail label="How heard" value={item.how_heard} />
      <Detail label="How heard detail" value={item.how_heard_detail} multiline />
    </>
  );
}

function SpeakingDetails({ item }) {
  return (
    <>
      <Detail label="Role or title" value={item.role_or_title} />
      <Detail label="Event name" value={item.event_name} />
      <Detail label="Event date" value={item.event_date} />
      <Detail label="Event location" value={item.event_location} />
      <Detail label="Audience size" value={item.audience_size} />
      <Detail label="Audience description" value={item.audience_description} multiline />
      <Detail label="Engagement type" value={item.engagement_type} />
      <Detail label="Fee range" value={item.fee_range} />
      <Detail label="Travel covered" value={item.travel_covered} />
      <Detail label="Notes" value={item.notes} multiline />
    </>
  );
}
